
import { useEffect, useState } from "react";
import { FaCamera } from "react-icons/fa";
import {
  getProfile,
  getProfileAnother,
  postOtherFields,
  postProfile,
} from "../API/res";


const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || "https://api.instilit.com";
const formatMediaUrl = (path) =>
  path?.startsWith("http")
    ? path
    : `${MEDIA_URL.replace(/\/$/, "")}/${path.replace(/^\/+/, "")}`;

const BasicDetailsSection = () => {
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState("");
  const [resume, setResume] = useState(null);
  const [bio, setBio] = useState("");
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    landmark: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    userType: "student",
  });

  const originalDataRef = useState({})[0];

  const fetchProfile = async () => {
    try {
      const data = await getProfile();
      const formState = {
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        email: data.email || "",
        phone: data.phone || "",
        userType: data.userType || "student",
        address: data.address || "",
        landmark: data.landmark || "",
        city: data.city || "",
        state: data.state || "",
        country: data.country || "",
        pincode: data.pincode || "",
      };
      setFormData(formState);
      Object.assign(originalDataRef, { ...formState });
    } catch (err) {
      console.warn("No existing basic profile found.", err);
    }
  };
  const fetchAdditionalProfileData = async () => {
  try {
    const extra = await getProfileAnother();
    const profile = extra?.profile || {};

    setBio(profile.bio || "");
    setProfilePicUrl(profile.profile_picture || "");
    setResume(profile.resume || null);

    Object.assign(originalDataRef, {
      bio: profile.bio,
      profile_picture: profile.profile_picture,
      resume: profile.resume,
    });
  } catch (err) {
    console.warn("Error loading additional profile data:", err);
  }
};


  useEffect(() => {
    fetchProfile();
    fetchAdditionalProfileData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, setter, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("File too large. Max size is 10MB.");
      return;
    }

    if (fileType === "resume" && file.type !== "application/pdf") {
      alert("Please upload a PDF resume.");
      return;
    }

    if (
      fileType === "profile_picture" &&
      !["image/jpeg", "image/png", "image/jpg"].includes(file.type)
    ) {
      alert("Only JPEG, JPG, or PNG files are allowed for profile picture.");
      return;
    }

    setter(file);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const registerPayload = {
        username: formData.email.split("@")[0],
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone,
        userType: formData.userType,
        address: formData.address,
        landmark: formData.landmark,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        pincode: formData.pincode,
      };
      await postOtherFields(registerPayload);

      const profileForm = new FormData();
      profileForm.append("bio", bio || "");
      if (profilePic) profileForm.append("profile_picture", profilePic);
      if (resume) profileForm.append("resume", resume);

      await postProfile(profileForm);

      await fetchAdditionalProfileData();
      setProfilePic(null);
      alert("Details saved successfully!");
      setEditMode(false);
    } catch (err) {
      console.error("Details submission failed:", err);
      alert("Failed to save details.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setFormData({ ...originalDataRef });
    setBio(originalDataRef.bio || "");
    setProfilePic(null);
    setResume(originalDataRef.resume || null);
  };

  return (

    <div className="bg-white p-6 rounded-2xl shadow-lg space-y-6 max-w-3xl mx-auto border">
  {/* Profile Picture */}
  <div className="flex flex-col items-center">
    <div className="relative w-28 h-28">
      <img
        src={
          profilePic
            ? URL.createObjectURL(profilePic)
            : profilePicUrl
            ? formatMediaUrl(profilePicUrl)
            : "https://dummyimage.com/150x150/ccc/fff&text=Profile"
        }
        alt="Profile"
        className="w-28 h-28 rounded-full object-cover border-4 border-blue-500 shadow-md"
      />
      {editMode && (
        <>
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 rounded-full cursor-pointer">
            <FaCamera className="text-gray-700 text-xl" />
          </div>
          <input
            type="file"
            accept="image/*"
            className="hidden"
            id="profile-upload"
            onChange={(e) => handleFileChange(e, setProfilePic, "profile_picture")}
          />
          <button
            onClick={() => document.getElementById("profile-upload").click()}
            className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
          >
            {profilePic ? "Change Photo" : "Upload Photo"}
          </button>
        </>
      )}
    </div>
  </div>

  {/* Resume */}
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1">Resume</label>
    {editMode ? (
      <>
        <input
          type="file"
          accept="application/pdf"
          onChange={(e) => handleFileChange(e, setResume, "resume")}
          className="block w-full text-sm border rounded px-3 py-2"
        />
        {resume && typeof resume !== "string" && (
          <p className="text-xs text-green-700 mt-1">Selected: {resume.name}</p>
        )}
      </>
    ) : typeof resume === "string" ? (
      <a
        href={formatMediaUrl(resume)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline text-sm"
      >
        View Resume
      </a>
    ) : (
      <p className="text-sm text-gray-500">No resume uploaded.</p>
    )}
  </div>

  {/* Bio */}
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1">Bio</label>
    {editMode ? (
      <textarea
        value={bio}
        onChange={(e) => setBio(e.target.value)}
        className="w-full p-2 border rounded text-sm"
        rows={3}
        placeholder="Write a short bio..."
      />
    ) : (
      <p className="text-sm text-gray-800">{bio || "No bio provided."}</p>
    )}
  </div>

  {/* User Details */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {Object.entries(formData).map(([key, value]) =>
      key === "username" ? null : (
        <div key={key}>
          <label className="block text-sm font-semibold text-gray-700 mb-1 capitalize">
            {key.replace(/([A-Z])/g, " $1")}
          </label>
          {editMode ? (
            <input
              type="text"
              name={key}
              value={value}
              onChange={handleInputChange}
              className="w-full p-2 border rounded text-sm"
            />
          ) : (
            <p className="text-sm text-gray-800">{value || "—"}</p>
          )}
        </div>
      )
    )}
  </div>

  {/* Action Buttons */}
  <div className="pt-4 flex gap-4 justify-end">
    {editMode ? (
      <>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded transition ${
            loading ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {loading ? "Saving..." : "Save"}
        </button>
        <button
          onClick={handleCancel}
          className="bg-gray-400 hover:bg-gray-500 text-white px-5 py-2 rounded transition"
        >
          Cancel
        </button>
      </>
    ) : (
      <button
        onClick={() => setEditMode(true)}
        className="bg-yellow-500 hover:bg-yellow-600 text-white px-5 py-2 rounded transition"
      >
        Edit
      </button>
    )}
  </div>
</div>
  );
};

export default BasicDetailsSection;
